import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { DeviceCommandRecord, IssueCommandInput, ActuatorSpecOutput } from '@grosync/common';
import { fetchDeviceCommands, sendDeviceCommand } from '../../../api/commands';
import { useDeviceModels } from '../../device-models/useDeviceModels';

const STATUS_META: Record<string, { label: string; c: string }> = {
  pending: { label: '대기', c: 'var(--gs-warning)' },
  sent: { label: '전송됨', c: 'var(--gs-warning)' },
  acked: { label: '완료', c: 'var(--gs-primary-50)' },
  failed: { label: '실패', c: 'var(--md-sys-color-error)' },
  expired: { label: '만료', c: 'var(--md-sys-color-on-surface-variant)' },
};

const pad2 = (n: number): string => String(n).padStart(2, '0');
function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

const btn: React.CSSProperties = {
  height: 34,
  padding: '0 14px',
  borderRadius: 9999,
  cursor: 'pointer',
  border: '1px solid var(--md-sys-color-outline-variant)',
  background: 'transparent',
  color: 'var(--md-sys-color-on-surface)',
  font: '600 12px/1 var(--gs-font-kr)',
};

export default function DeviceControlPanel({
  deviceId,
  deviceModelId,
  model,
}: {
  deviceId: string;
  deviceModelId: string | undefined;
  /** 2 = 환경·생육(센서cam 이 같은 장비 번호로 붙어 있음) → 「지금 촬영」 표시 */
  model?: 1 | 2 | null;
}): React.ReactElement {
  const qc = useQueryClient();
  const { models } = useDeviceModels();
  const actuators: ActuatorSpecOutput[] = useMemo(() => {
    const m = models.find((x) => x.code === deviceModelId);
    return m?.actuators ?? [];
  }, [models, deviceModelId]);

  const [pwm, setPwm] = useState<Record<string, number>>({});

  const historyQ = useQuery<DeviceCommandRecord[]>({
    queryKey: ['device-commands', deviceId],
    queryFn: () => fetchDeviceCommands(deviceId),
    refetchInterval: 3_000,
  });

  const mutation = useMutation({
    mutationFn: (body: IssueCommandInput) => sendDeviceCommand(deviceId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['device-commands', deviceId] }),
  });

  const send = (body: IssueCommandInput): void => mutation.mutate(body);
  const busy = mutation.isPending;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ font: '700 14px/20px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginBottom: 10 }}>
          장비 제어
        </div>

        {actuators.length === 0 ? (
          <div style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
            이 장비 모델에 제어 채널이 없습니다. 장비모델에서 액추에이터를 추가하세요.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {actuators.map((a) => (
              <div key={a.channel} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ minWidth: 120, font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                  {a.displayName || a.channel}
                  <span style={{ marginLeft: 6, font: '400 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{a.channel}</span>
                </span>

                {a.type === 'relay' ? (
                  <>
                    <button type="button" disabled={busy} style={btn}
                      onClick={() => send({ type: 'ACTUATE', params: { kind: 'relay', channel: a.channel, state: 'on' } })}>
                      ON
                    </button>
                    <button type="button" disabled={busy} style={btn}
                      onClick={() => send({ type: 'ACTUATE', params: { kind: 'relay', channel: a.channel, state: 'off' } })}>
                      OFF
                    </button>
                  </>
                ) : (
                  <>
                    <input type="range" min={0} max={100} value={pwm[a.channel] ?? 0}
                      onChange={(e) => setPwm((p) => ({ ...p, [a.channel]: Number(e.target.value) }))}
                      style={{ width: 140 }} />
                    <span className="gs-num" style={{ width: 40, font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                      {pwm[a.channel] ?? 0}%
                    </span>
                    <button type="button" disabled={busy} style={btn}
                      onClick={() => send({ type: 'ACTUATE', params: { kind: 'pwm', channel: a.channel, duty: pwm[a.channel] ?? 0 } })}>
                      적용
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button type="button" disabled={busy} style={btn} onClick={() => send({ type: 'REPORT' })}>
            상태 보고 요청
          </button>
          {model === 2 && (
            <button type="button" disabled={busy} style={btn} onClick={() => send({ type: 'CAPTURE' })}>
              지금 촬영
            </button>
          )}
          <button type="button" disabled={busy} style={{ ...btn, color: 'var(--md-sys-color-error)' }}
            onClick={() => {
              if (window.confirm('장비를 재부팅할까요?')) send({ type: 'REBOOT' });
            }}>
            재부팅
          </button>
        </div>
        {mutation.isError && (
          <div style={{ marginTop: 8, font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-error)' }}>
            전송 실패: {(mutation.error as Error).message}
          </div>
        )}
      </div>

      <div>
        <div style={{ font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginBottom: 8 }}>
          최근 명령
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(historyQ.data ?? []).length === 0 ? (
            <div style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
              명령 이력이 없습니다.
            </div>
          ) : (
            (historyQ.data ?? []).slice(0, 20).map((c) => {
              const sm = STATUS_META[c.status] ?? STATUS_META.pending;
              const params =
                c.type === 'ACTUATE' && c.payload && (c.payload as { params?: unknown }).params
                  ? ` · ${JSON.stringify((c.payload as { params: unknown }).params)}`
                  : '';
              return (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--md-sys-color-outline-variant)' }}>
                  <span className="gs-num" style={{ width: 110, font: '400 11px/15px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{fmtTime(c.issuedAt)}</span>
                  <span style={{ flex: 1, font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                    {c.type}{params}
                  </span>
                  <span style={{ font: '700 11px/15px var(--gs-font-kr)', color: sm.c }}>{sm.label}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
